import React, { useMemo, useState, useEffect } from "react";
import styles from "./TransactionsTableComponent.module.css";
import TransactionsTableRow from "../TransactionsTableRow/TransactionsTableRow";

/* ---------- config ---------- */
/** Change to ":" if you want 7 -> 7:00 visually */
const AMOUNT_FRACTION_SEPARATOR = ".";

/* ---------- helpers kept/exported (other files depend on these) ---------- */
function inferColumns(items) {
  const cols = [];
  const seen = new Set();
  for (const row of items || []) {
    if (row && typeof row === "object") {
      for (const k of Object.keys(row)) {
        if (!seen.has(k)) { seen.add(k); cols.push(k); }
      }
    }
  }
  return cols;
}

/** Parse arbitrary amount strings into a Number. */
export function parseAmountValue(v) {
  if (typeof v === "number") return v;
  if (v == null) return NaN;
  let s = String(v).trim();
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[^\d.,-]+/g, "");
  if (s.includes(",") && s.includes(".")) s = s.replace(/,/g, "");
  else if (s.includes(",") && !s.includes(".")) s = s.replace(/,/g, ".");
  const n = parseFloat(s);
  return Number.isNaN(n) ? NaN : (neg ? -n : n);
}

/** Format a numeric amount with 2 fractional digits and thousands separators. */
export function formatAmountDisplay(num) {
  if (!Number.isFinite(num)) return "";
  const isNeg = num < 0;
  const abs = Math.abs(num);

  const fixed = abs.toFixed(2); // "1234.50"
  const [intPart, fracPart] = fixed.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const out = `${grouped}${AMOUNT_FRACTION_SEPARATOR}${fracPart}`;
  return isNeg ? `-${out}` : out;
}

/* ---------- constants ---------- */
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_OPTIONS_DEFAULT = [10, 25, 50, 100, 250];

/**
 * Props:
 * - items, columns, emptyMessage, density, striped, stickyFirst, className
 * - wrapDescription (bool), tagsAsChips (bool)
 * - initialRowsPerPage, rowsPerPageOptions, onRowsPerPageChange(n)
 * - selectable, selectedIds, onToggleRow(id, checked, row), onToggleAll(checked, visibleIds)
 * - getRowId(row, absoluteIndex)
 */
export default function TransactionsTableComponent({
  items,
  columns,
  emptyMessage = "No transactions.",
  density = "cozy",
  striped = true,
  stickyFirst = true,
  className = "",
  wrapDescription = false,
  tagsAsChips = true,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS_DEFAULT,
  onRowsPerPageChange,

  selectable = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
  getRowId,

  inlineEditEnabled = false,
  onCommitPatch,
  accounts = [],
  accountOptions = [],
}) {
  const data = Array.isArray(items) ? items : [];

  const inferredCols = useMemo(() => inferColumns(data), [data]);
  const visibleCols = useMemo(() => {
    if (Array.isArray(columns)) return columns;
    return inferredCols;
  }, [columns, inferredCols]);

  /* ---------- pagination ---------- */
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [page, setPage] = useState(0);

  // keep in sync if parent changes the initial value (e.g., from settings modal)
  useEffect(() => {
    const n = Number(initialRowsPerPage);
    if (Number.isFinite(n) && n > 0 && n !== rowsPerPage) {
      setRowsPerPage(n);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const start = page * rowsPerPage;
  const end = Math.min(start + rowsPerPage, data.length);
  const pageRows = data.slice(start, end);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));
  const handleRowsPerPageChange = (e) => {
    const next = Number(e.target.value) || DEFAULT_ROWS_PER_PAGE;
    setRowsPerPage(next);
    setPage(0);
    onRowsPerPageChange && onRowsPerPageChange(next);
  };

  /* ---------- classes ---------- */
  const wrapperClasses = [
    "pp-table",
    stickyFirst ? "is-sticky-first" : "",
    density === "comfortable" ? "is-comfortable" : "",
    density === "compact" ? "is-compact" : "",
    density === "cozy" ? "is-cozy" : "",
    className,
  ].filter(Boolean).join(" ");

  if (data.length === 0) {
    return (
      <div className={`${styles.wrap} ${wrapperClasses}`}>
        <div className="pp-table-empty">
          <div className="mb-1">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  const rowId = (row, idx) => (getRowId ? getRowId(row, idx) : (row?.id ?? idx));
  const pageIds = pageRows.map((r, i) => rowId(r, start + i));
  const allOnPageSelected = selectable && pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someOnPageSelected = selectable && !allOnPageSelected && pageIds.some((id) => selectedIds.includes(id));

  return (
    <div className={`${styles.wrap} ${wrapperClasses}`}>
      <div className={`pp-table-scroll ${styles.transactionsTable}`}>
        <table className={`table ${striped ? "table-striped" : ""} mb-0 ${styles.table}`}>
          <thead className="table-light">
            <tr>
              {selectable && (
                <th className={styles.headerCell} style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    aria-label="Select all"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = someOnPageSelected; }}
                    onChange={(e) => onToggleAll?.(e.target.checked, pageIds)}
                  />
                </th>
              )}
              {visibleCols.map((key) => {
                const headerClass = [
                  "is-mono",
                  "truncate", // keep headers truncated
                  styles.headerCell,
                  /date/i.test(key) ? styles.dateCell : "",
                  /description|memo|note/i.test(key) ? styles.descriptionCell : "",
                  /tag/i.test(key) ? styles.tagsCell : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={key} className={headerClass} title={key}>
                    {key}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {pageRows.map((row, i) => {
              const absoluteIndex = start + i;
              const id = rowId(row, absoluteIndex);
              const isChecked = selectable && selectedIds.includes(id);
              return (
                <TransactionsTableRow
                  key={id}
                  id={id}
                  row={row}
                  absoluteIndex={absoluteIndex}
                  visibleCols={visibleCols}
                  selectable={selectable}
                  isChecked={!!isChecked}
                  onToggleRow={onToggleRow}
                  wrapDescription={!!wrapDescription}
                  tagsAsChips={!!tagsAsChips}
                  inlineEditEnabled={inlineEditEnabled}
                  onCommitPatch={onCommitPatch}
                  accounts={accounts}
                  accountOptions={accountOptions}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className={`${styles.controls} mt-2`}>
        <div className="small text-secondary">
          {data.length > 0 ? (
            <>Showing <strong>{start + 1}</strong>–<strong>{end}</strong> of <strong>{data.length}</strong></>
          ) : (<>No rows</>)}
        </div>

        <div className={`d-flex align-items-center gap-3 flex-nowrap ${styles.paginationGroup}`}>
          <label className="small text-secondary mb-0" style={{ minWidth: "2.5rem" }}>Rows</label>
          <select className="form-select form-select-sm" value={rowsPerPage} onChange={handleRowsPerPageChange}>
            {rowsPerPageOptions.map((n) => (<option key={n} value={n}>{n}</option>))}
          </select>

          <div className="d-flex align-items-center gap-2 flex-nowrap">
            <button type="button" className={`btn btn-sm ${page === 0 ? "btn-outline-secondary" : "btn-primary"}`} onClick={handlePrev} disabled={page === 0} aria-label="Previous page">‹</button>
            <span className="small text-secondary" style={{ minWidth: "5rem" }}>Page {page + 1} / {totalPages}</span>
            <button type="button" className={`btn btn-sm ${page >= totalPages - 1 ? "btn-outline-secondary" : "btn-primary"}`} onClick={handleNext} disabled={page >= totalPages - 1} aria-label="Next page">›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
